import * as React from "react";
import { LoadingState } from "@order-analyzer/ui";

const RELOAD_GUARD_KEY = "oa-chunk-reload-once";

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unexpected token '<'/i.test(message) ||
    /dynamically imported module/i.test(message)
  );
}

/**
 * Fängt fehlgeschlagene React.lazy()-Chunk-Ladevorgänge ab, wie sie auftreten, wenn ein Tab mit
 * dem alten Stand von index.html länger offen bleibt und nach einem Deploy einen Chunk unter
 * seinem alten (nicht mehr existierenden) Hash-Namen nachlädt - nginx liefert dafür per
 * SPA-Fallback index.html statt JS zurück, was als "Unexpected token '<'"-Fehler aufschlägt.
 * Statt den Nutzer mit einer dauerhaft hängenden Seite ("... wird geladen") zurückzulassen, wird
 * einmalig automatisch neu geladen (sessionStorage-Guard verhindert eine Reload-Schleife, falls der
 * Fehler aus einem anderen Grund weiter besteht).
 */
export class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(error: unknown) {
    if (isChunkLoadError(error)) return { failed: true };
    throw error;
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) return;
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    window.location.reload();
  }

  render() {
    if (this.state.failed) return <LoadingState label="Neue Version wird geladen ..." />;
    return this.props.children;
  }
}
